import { Webhook } from "../common/Webhook";
import { FulfillmentItem } from "../fulfillmentItem";
import { FulfillmentLabel } from "../fulfillmentLabel";
import { Order } from "../order";
import { WebhookPayload } from "../payment";

export enum FulfillmentStatus {
    PENDING = 'pending',
    SHIPPED = 'shipped',
    DELIVERED = 'delivered',
    CANCELLED = 'cancelled',
}

export type Fulfillment = {
    id: string;
    code: string;
    accountId: string;
    orderId: string;
    accountBranchId: string;
    deliveryOptionId: string
    items: FulfillmentItem[];
    labels: FulfillmentLabel[];
    status: FulfillmentStatus;
    data: Record<string, unknown> | null;
    shippedAt: Date | null;
    deliveredAt: Date | null;
    cancelledAt: Date | null;
    createdAt: Date;
    updatedAt: Date;
    deletedAt?: Date;
}

// Fulfillment Provider types
export type FulfillmentDeliveryOption = {
  // useful to store the fulfillment option id, to later interact with that service level
  id: string
  // Name to show to the admin when creating the delivery option (could also be used in the storefront)
  name: string
  // Pickup locations for PICKUP delivery type options
  pickupLocations?: PickupLocation[]
  [key: string]: unknown
}

// Pickup Location types
export interface PickupLocation {
  id: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode?: string;
  };
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  hours?: string;
  phone?: string;
  additionalInfo?: Record<string, unknown>;
}


// Recollection specific types
export type FulfillmentRecollectionMode = 'RECOLLECTION' | 'DROP_OFF';

// Simplified scheduling - just define when recollections are possible
export type FulfillmentRecollectionSchedule = {
  dayOfWeek: number; // 0-6 (Sun-Sat)
  startHour: number; // 9 (for 9:00 AM)
  endHour: number; // 17 (for 5:00 PM)
  intervalMinutes?: number; // Optional: 30, 60, etc. Default could be 60
}

export type FulfillmentRecollectionCapabilities = {
  supportsRecollection: boolean;
  supportsDropOff: boolean;
  allowsScheduling: boolean;
  recollectionSchedule?: FulfillmentRecollectionSchedule[];
  leadTimeHours: number; // Minimum hours before recollection
  maxAdvanceDays: number; // Maximum days in advance to schedule
  dropOffLocationsUrl?: string; // Link to provider's page showing drop-off locations
}

// Recollection configuration for a specific fulfillment request
export type FulfillmentRecollectionConfig = {
  mode: FulfillmentRecollectionMode;
  scheduledDateTime?: Date; // Required when allowsScheduling=true and mode includes recollection
  recollectionAddress?: {
    street: string;
    city: string;
    state: string;
  };
  contactInfo?: {
    name: string;
    phone: string;
    email?: string;
  };
  specialInstructions?: string;
}

// Input for creating a fulfillment with third-party provider
export type FulfillmentProviderCreateInput = {
  data: Record<string, unknown>; // Order delivery method data
  items: FulfillmentItem[];
  order: Order;
  fulfillment: Fulfillment;
  recollectionConfig?: FulfillmentRecollectionConfig;
}

// Output from creating a fulfillment with third-party provider
export type FulfillmentProviderCreateOutput = {
  // Provider-specific data to store in fulfillment entity
  data?: Record<string, unknown>;
  // Array of shipping labels/tracking information
  labels: {
    trackingNumber: string;
    trackingUrl?: string;
    label?: {
      url?: string;
      base64?: string;
      format?: 'pdf' | 'png' | 'zpl'; // Label format
    };
  }[];
}

export type FulfillmentProviderProcessWebhookInput = WebhookPayload & {
  // TODO: Put the actual service type once we move it from /api to here
  fulfillmentService: any,
}

export type FulfillmentProviderProcessWebhookOutput = {
  fulfillmentId: string,
  fulfillmentStatus: FulfillmentStatus,
  data: Record<string, any>
}

// Provider-level types (entity-agnostic, reusable across projects)
export type FulfillmentProviderKey = 'MANUAL_FULFILLMENT' | 'DAC';

// Provider context for initialization
export type FulfillmentProviderContext = {
  data: Record<string, any>;
}

// Base adapter interface that all fulfillment providers must implement
export interface FulfillmentProviderAdapter {
  readonly key: FulfillmentProviderKey;
  listDeliveryOptions(): Promise<FulfillmentDeliveryOption[]>;
  // Useful to execute when trying to calculate a delivery option price in the checkout,
  // to validate it can be calculated
  canCalculate(data: Record<string, unknown>): Promise<boolean>
  getRecollectionCapabilities(): Promise<FulfillmentRecollectionCapabilities>;
  // Create fulfillment with third-party provider
  createFulfillment(input: FulfillmentProviderCreateInput): Promise<FulfillmentProviderCreateOutput>;
  processWebhook(input: FulfillmentProviderProcessWebhookInput): Promise<FulfillmentProviderProcessWebhookOutput | null>
  // cancel(input: FulfillmentProviderCancelInput): Promise<FulfillmentProviderCancelOutput>;
}